// Admin Service Selection Modal - Adapted from App.jsx ServiceSelectionModal
import React, { useEffect } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType, IconComponent } from '../../types/admin';

interface SelectedCompany {
  displayName: string;
  [key: string]: unknown;
}

interface AdminServiceSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectService: (serviceId: string) => void;
  selectedCompany?: SelectedCompany | null;
  brand: BrandConfig;
  Icons: IconsType;
}

interface ServiceConfig {
  id: string;
  label: string;
  description: string;
  active: boolean;
}

const AdminServiceSelectionModal: React.FC<AdminServiceSelectionModalProps> = ({ isOpen, onClose, onSelectService, selectedCompany, brand, Icons }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Service configuration matching BrandConfig — IDs match OrderType enum values
  const services: ServiceConfig[] = [
    { id: 'e-filing', label: 'Court Filing', description: 'Electronic court filing with any California court', active: true },
    { id: 'process-serving', label: 'Process Serving', description: 'Serve legal documents to parties', active: false },
    { id: 'document-retrieval', label: 'Document Retrieval', description: 'Retrieve copies of filed documents', active: false },
    { id: 'courtesy-copy-delivery', label: 'Courtesy Copy Delivery', description: 'Deliver courtesy copies to judges or departments', active: false },
    { id: 'secretary-of-state-filing', label: 'Secretary of State Filing', description: 'File documents with the Secretary of State', active: false },
    { id: 'county-recording', label: 'County Recording', description: 'Record documents with the county recorder', active: false },
    { id: 'skip-trace', label: 'Skip Trace', description: 'Locate individuals for service of process', active: false },
    { id: 'eservice', label: 'eService', description: 'Electronic service of documents to parties', active: false },
    { id: 'concierge', label: 'Concierge Service', description: 'Full-service litigation support', active: false },
  ];

  const serviceIcons: Record<string, IconComponent> = {
    'e-filing': Icons.EService || Icons.Document,
    'process-serving': Icons.ProcessServing || Icons.Document,
    'document-retrieval': Icons.Document,
    'courtesy-copy-delivery': Icons.Delivery || Icons.Document,
    'secretary-of-state-filing': Icons.Building || Icons.Document,
    'county-recording': Icons.Document,
    'skip-trace': Icons.Locator || Icons.Search,
    'eservice': Icons.EService || Icons.Mail,
    'concierge': Icons.Concierge || Icons.User,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        style={{ backgroundColor: brand.colors.bgCard || 'white' }}
        data-testid="admin-service-modal"
      >
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold" style={{ color: brand.colors.secondary }}>
              Place New Order
            </h2>
            {selectedCompany && (
              <p className="text-sm mt-1" style={{ color: brand.colors.textLight }}>
                For: <span className="font-medium" style={{ color: brand.colors.text }}>{selectedCompany.displayName}</span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors min-h-touch min-w-touch flex items-center justify-center"
          >
            <Icons.X className="w-5 h-5" style={{ color: brand.colors.textLight }} />
          </button>
        </div>

        <p className="text-sm mb-4 sm:mb-6" style={{ color: brand.colors.textLight }}>
          Select the type of service to create:
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {services.map(service => {
            const ServiceIcon = serviceIcons[service.id] || Icons.Document;
            return (
              <button
                key={service.id}
                onClick={service.active ? () => {
                  onSelectService(service.id);
                  onClose();
                } : undefined}
                disabled={!service.active}
                className={`flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border-2 transition-all text-left min-h-touch ${
                  service.active
                    ? 'hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer'
                    : 'opacity-60 cursor-not-allowed'
                }`}
                style={{ borderColor: brand.colors.border }}
                data-testid={`service-option-${service.id}`}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: brand.colors.bgLight }}
                >
                  <ServiceIcon className="w-5 h-5" style={{ color: brand.colors.link }} />
                </div>
                <div>
                  <div className="font-medium flex items-center gap-2" style={{ color: brand.colors.secondary }}>
                    {service.label}
                    {!service.active && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-normal"
                        style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}
                        data-testid={`coming-soon-badge-${service.id}`}
                      >
                        Coming soon
                      </span>
                    )}
                  </div>
                  <div className="text-sm mt-1" style={{ color: brand.colors.textLight }}>
                    {service.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminServiceSelectionModal;
